import React, { useEffect, useState } from "react";
import { api, packTrackToPlayable } from "@/lib/api";
import { TrackList } from "@/components/TrackRow";
import { CompactTrackList } from "@/components/CompactTrackList";
import { GenreFilter } from "@/components/GenreFilter";
import { PageLoading, LoadError } from "@/components/LoadState";
import { splitGenres } from "@/components/GenrePill";
import { useSearchParams } from "react-router-dom";

export default function CategoryPage({ category, title, subtitle, config }) {
  const [all, setAll] = useState([]);
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const selected = genre.split(",").map((g) => g.trim().toLowerCase()).filter(Boolean);
  const genres = Object.values(all.reduce((acc, t) => { splitGenres(t.genre).forEach((g) => { const k = g.toLowerCase(); acc[k] = acc[k] ? { ...acc[k], count: acc[k].count + 1 } : { name: g, count: 1 }; }); return acc; }, {})).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const tracks = selected.length ? all.filter((t) => splitGenres(t.genre).some((g) => selected.includes(g.toLowerCase()))) : all;
  const toggleGenre = (name) => { const k = name.toLowerCase(); const next = selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]; const names = next.map((x) => genres.find((g) => g.name.toLowerCase() === x)?.name || x); names.length ? setParams({ genre: names.join(",") }) : setParams({}); };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true); setError(false);
      try {
        const [res, packs] = await Promise.all([api.get(`/tracks?category=${category}&limit=5000`), (category === "remix" || category === "mashup") ? api.get("/packs") : Promise.resolve({ data: [] })]);
        const rx = new RegExp(category, "i");
        const pistas = packs.data.flatMap((pack) => (pack.tracks || []).filter((pt) => !pt.track_id && rx.test(pt.title)).map((pt) => packTrackToPlayable(pack, pt)));
        setAll([...res.data, ...pistas]);
      } catch { setError(true); }
      setLoading(false);
    })();
  }, [category, attempt]);

  const isBig = (t) => (selected.length ? (t.highlight_genres || []).some((g) => selected.includes(g.toLowerCase())) : !!t.is_highlighted);
  const bigSolo = tracks.filter((t) => isBig(t) && !t.pack_id);
  const bigPack = tracks.filter((t) => isBig(t) && t.pack_id);
  const rest = [...tracks].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const hlDate = (t) => new Date(t.highlighted_at || t.created_at || 0);
  const big = [...bigSolo, ...bigPack].sort((a, b) => hlDate(b) - hlDate(a));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="mb-10">
        <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">{subtitle}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white">
          {title}
        </h1>
        {!loading && !error && <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mt-3" data-testid={`${category}-count`}>{tracks.length} tracks{genre ? ` · ${genre.split(",").join(" + ")}` : ""}</div>}
      </div>
      {genres.length > 0 && (
        <GenreFilter prefix={category} genres={genres} selected={selected} onToggle={toggleGenre} onClear={() => setParams({})} />
      )}

      {loading ? (
        <PageLoading testid={`${category}-loading`} />
      ) : error ? (
        <LoadError onRetry={() => setAttempt((a) => a + 1)} testid={`${category}-error`} />
      ) : tracks.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-24 text-center text-white/40">
          Aún no hay contenido en esta sección.
        </div>
      ) : (
        <div className="space-y-12">
          {big.length > 0 && (
            <div data-testid={`${category}-highlights`}>
              <div className="font-mono text-[10px] text-red-500 uppercase tracking-[0.3em] mb-3">Destacados · {big.length}</div>
              <TrackList tracks={big} config={config} perPage={6} grid testid={`${category}-grid`} />
            </div>
          )}
          <div data-testid={`${category}-list`}>
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-[0.3em] mb-3">Todos · orden de subida</div>
            <CompactTrackList tracks={rest} />
          </div>
        </div>
      )}
    </div>
  );
}
